import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { SectionBadge } from './SectionBadge';
import type { CompanyInfo } from '@generated/types';

interface CompanyCardProps {
  company: CompanyInfo;
  onChange: (patch: Partial<CompanyInfo>) => void;
  showErrors: boolean;
  companyInvalid: boolean;
  contactNameInvalid: boolean;
  emailInvalid: boolean;
  shippingOriginInvalid: boolean;
}

export function CompanyCard({
  company,
  onChange,
  showErrors,
  companyInvalid,
  contactNameInvalid,
  emailInvalid,
  shippingOriginInvalid,
}: CompanyCardProps) {
  return (
    <section className="mt-5 rounded-xl border border-border bg-card p-6 shadow-[var(--shadow-stack)] sm:p-8">
      <div className="flex items-start gap-3">
        <SectionBadge number={1} />
        <div>
          <h2 className="font-[family-name:var(--font-heading)] text-xl font-medium text-foreground">Your company</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Where the material ships from, and who we work with to get it moving.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="company-name" className="text-xs font-medium text-foreground">
            Company
          </Label>
          <Input
            id="company-name"
            aria-invalid={showErrors && companyInvalid}
            className="mt-1.5"
            value={company.company}
            onChange={(e) => onChange({ company: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="contact-name" className="text-xs font-medium text-foreground">
            Your name
          </Label>
          <Input
            id="contact-name"
            aria-invalid={showErrors && contactNameInvalid}
            className="mt-1.5"
            value={company.contactName}
            onChange={(e) => onChange({ contactName: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="company-email" className="text-xs font-medium text-foreground">
            Email
          </Label>
          <Input
            id="company-email"
            type="email"
            aria-invalid={showErrors && emailInvalid}
            className="mt-1.5"
            value={company.email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="company-phone" className="text-xs font-medium text-foreground">
            Phone
          </Label>
          <Input
            id="company-phone"
            type="tel"
            className="mt-1.5"
            value={company.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            placeholder="(555) 555-0123"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="shipping-origin" className="text-xs font-medium text-foreground">
            Shipping Origin Address
          </Label>
          <Input
            id="shipping-origin"
            aria-invalid={showErrors && shippingOriginInvalid}
            className="mt-1.5"
            value={company.shippingOrigin}
            onChange={(e) => onChange({ shippingOrigin: e.target.value })}
            placeholder="Street address, city, state, ZIP"
          />
        </div>
      </div>
    </section>
  );
}
